import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { boards, github, type Board } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { CreateBoardModal } from '../components/CreateBoardModal';
import { getBoardColorHex, timeAgo } from '../lib/utils';

export function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['boards'],
    queryFn: boards.list,
    enabled: !authLoading && !!user, // Wait for auth before fetching
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; templateId: string; color?: string; githubRepo?: string; githubTokenId?: string }) => {
      const { githubRepo, githubTokenId, color, ...boardData } = data;
      const res = await boards.create(boardData);
      if (res.data && color) {
        // Persist color via PUT since POST /boards doesn't accept it.
        const next = { ...res.data, settings: { ...res.data.settings, color } };
        await boards.update(res.data.id, { settings: next.settings } as any);
        res.data = next;
      }
      if (res.data && githubRepo) {
        const [owner, repo] = githubRepo.split('/');
        await github.boardSettings.set(res.data.id, { owner, repo, tokenId: githubTokenId });
      }
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      toast('Board created!', 'success');
      setShowCreate(false);
      if (res.data) navigate(`/boards/${res.data.id}`);
    },
    onError: () => toast('Failed to create board', 'error'),
  });

  const boardList: Board[] = data?.data || [];

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-400">Your boards and projects</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-500"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Board
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-400">
          Failed to load boards. Make sure the API is running.
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && boardList.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-800 py-20">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-800">
            <svg className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-300">No boards yet</h3>
          <p className="mt-1 text-xs text-gray-500">Create your first board to get started</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
          >
            Create Board
          </button>
        </div>
      )}

      {/* Board grid */}
      {boardList.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {boardList.map((board) => (
            <button
              key={board.id}
              onClick={() => navigate(`/boards/${board.id}`)}
              className="group cursor-pointer rounded-xl border border-gray-800 bg-surface-850 p-5 text-left transition-all hover:border-gray-700 hover:bg-surface-800"
              style={{ borderLeft: `3px solid ${getBoardColorHex(board.settings?.color as string | undefined)}` }}
            >
              <h3 className="text-sm font-semibold text-gray-100 group-hover:text-primary-400">
                {board.name}
              </h3>
              {board.description && (
                <p className="mt-1 line-clamp-2 text-xs text-gray-500">{board.description}</p>
              )}
              <div className="mt-4 flex items-center gap-3 text-[10px] text-gray-600">
                <span>{board.columns.length} columns</span>
                <span>Updated {timeAgo(board.updatedAt)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateBoardModal
          onSubmit={(data) => createMutation.mutate(data)}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
